// The Send dialog behind each table's "Send" button on the Waterfall page: the
// recipients, prefilled from the practice's weekly client email settings (or the
// signed-in admin for the team table), editable for this one send; for the client
// report, whether to include patient names (off by default: the names are the
// practice's own patients and the recipients list was just typed). POSTs
// /api/practices/<id>/digest/send; the server renders the table as HTML from the
// same figures the page shows, at the moment of sending, and audits the send.

import React, { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "";

export const TABLE_TITLES = {
  waterfall: "Waterfall to 98%",
  client: "Weekly report, client",
  team: "Weekly report, team",
};

export function parseRecipients(text) {
  return String(text || "")
    .split(/[,\n;]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

export async function fetchDeliverySettings(practiceId) {
  const response = await fetch(
    `${API_URL}/api/practices/${practiceId}/digest/delivery`,
    { credentials: "include" },
  );
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((body && body.error) || "Could not load the delivery settings");
  }
  return body;
}

export async function sendReport(practiceId, body) {
  const response = await fetch(
    `${API_URL}/api/practices/${practiceId}/digest/send`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  const out = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((out && out.error) || "The report could not be sent");
  }
  return out;
}

// `span` is what the server needs to find the figures: {week, week_end} for the
// two reports, {preset, from, to} for the Waterfall. `prefill` is the recipients
// the dialog opens with; `label` the week or range shown in the title.
export default function SendReportDialog({
  practiceId,
  table,
  label,
  span,
  prefill,
  onClose,
  onSent,
}) {
  const [recipients, setRecipients] = useState((prefill || []).join(", "));
  const [showNames, setShowNames] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setRecipients((prefill || []).join(", "));
  }, [prefill]);

  const list = parseRecipients(recipients);

  const send = async () => {
    if (busy || !list.length) return;
    setBusy(true);
    setError(null);
    try {
      const out = await sendReport(practiceId, {
        table,
        recipients: list,
        show_names: table === "client" ? showNames : false,
        ...(span || {}),
      });
      onSent(out);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="wf-modal-bg"
      role="dialog"
      aria-modal="true"
      aria-label={`Send ${TABLE_TITLES[table] || table}`}
      data-testid="send-report-dialog"
    >
      <div className="wf-modal wf-send-dialog">
        <div className="wf-modal-header">
          <div>
            <h2>Send {TABLE_TITLES[table] || table}</h2>
            <p>
              {label ? `${label} · ` : ""}
              emailed as HTML, the figures as of the moment of sending; the
              send is logged with your name, the recipients and the week.
            </p>
          </div>
          <button type="button" className="wf-close" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="wf-modal-body">
          {error ? (
            <div className="wf-message error" role="alert">
              <span>{error}</span>
              <button
                type="button"
                className="message-close"
                aria-label="Dismiss"
                onClick={() => setError(null)}
              >
                ×
              </button>
            </div>
          ) : null}
          <label className="wf-send-field">
            Recipients (comma-separated)
            <textarea
              rows={3}
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              disabled={busy}
              data-testid="send-report-recipients"
            />
          </label>
          {table === "client" ? (
            <label className="wf-custom" title="The practice's own patients; off, the table lists them by chart number or MRN">
              <input
                type="checkbox"
                checked={showNames}
                onChange={(e) => setShowNames(e.target.checked)}
                disabled={busy}
                data-testid="send-report-names"
              />{" "}
              Include patient names
            </label>
          ) : null}
          <div className="wf-toolbar">
            <button
              type="button"
              className="wf-btn primary"
              onClick={send}
              disabled={busy || !list.length}
              data-testid="send-report-submit"
            >
              {busy ? "Sending…" : `Send to ${list.length || "…"}`}
            </button>
            <button type="button" className="wf-btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
